import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faRefresh, faMobileScreen, faTrash, faEdit, faX, faBell, faPaperPlane, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeQuartz, GridApi } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../styles/ag-grid-custom.css';
import apiService from '../services/apiService';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

const Users: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUserDevices, setSelectedUserDevices] = useState<any[] | null>(null);
  const [devicesDialogOpen, setDevicesDialogOpen] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);

  // Notification state
  const [selectedDevicesForNotification, setSelectedDevicesForNotification] = useState<any[]>([]);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [notificationData, setNotificationData] = useState<{ key: string; value: string }[]>([]);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationSuccess, setNotificationSuccess] = useState('');
  const [currentUserForDevices, setCurrentUserForDevices] = useState<any>(null);
  const gridApiRef = useRef<GridApi | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllUsersWithDevices(true);
      const normalized = (data || []).map((u: any) => {
        const firstName = u.firstName || u.firstname || u.first_name || '';
        const lastName = u.lastName || u.lastname || u.last_name || '';
        const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
        const userId = u.userId || u.user_id || u.id || u.email || String(Math.random());
        const id = userId;
        return { ...u, id, userId, firstName, lastName, fullName };
      });
      setUsers(normalized);

      const roles = Array.from(new Set(normalized.map((u: any) => u.role).filter(Boolean)));
      setAvailableRoles(roles);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const showDevices = async (userId: string, user?: any) => {
    try {
      setLoadingDevices(true);
      setError('');
      setDevicesDialogOpen(true);
      setSelectedUserDevices(null);
      setCurrentUserForDevices(user || null);
      const devices = await apiService.getUserDevices(userId);
      setSelectedUserDevices(devices || []);
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to load devices');
      setSelectedUserDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const toggleDeviceSelection = (device: any) => {
    setSelectedDevicesForNotification(prev => {
      const exists = prev.some(d => d._id === device._id);
      if (exists) {
        return prev.filter(d => d._id !== device._id);
      }
      return [...prev, { ...device, userName: currentUserForDevices?.fullName || currentUserForDevices?.email }];
    });
  };

  const selectAllDevices = () => {
    if (!selectedUserDevices) return;
    const newDevices = selectedUserDevices
      .filter(d => d.enabled && d.fcmToken)
      .map(d => ({ ...d, userName: currentUserForDevices?.fullName || currentUserForDevices?.email }));
    setSelectedDevicesForNotification(prev => {
      const existingIds = new Set(prev.map(d => d._id));
      const toAdd = newDevices.filter(d => !existingIds.has(d._id));
      return [...prev, ...toAdd];
    });
  };

  const removeDeviceFromSelection = (deviceId: string) => {
    setSelectedDevicesForNotification(prev => prev.filter(d => d._id !== deviceId));
  };

  const clearAllSelectedDevices = () => {
    setSelectedDevicesForNotification([]);
  };

  const openNotificationModal = () => {
    setNotificationTitle('');
    setNotificationBody('');
    setNotificationData([]);
    setNotificationSuccess('');
    setNotificationModalOpen(true);
  };

  const addDataField = () => {
    setNotificationData(prev => [...prev, { key: '', value: '' }]);
  };

  const removeDataField = (index: number) => {
    setNotificationData(prev => prev.filter((_, i) => i !== index));
  };

  const updateDataField = (index: number, field: 'key' | 'value', value: string) => {
    setNotificationData(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const sendNotification = async () => {
    if (!notificationTitle.trim() || !notificationBody.trim()) {
      setError('Title and body are required');
      return;
    }

    if (selectedDevicesForNotification.length === 0) {
      setError('No devices selected');
      return;
    }

    const deviceTokens = selectedDevicesForNotification
      .filter(d => d.fcmToken)
      .map(d => d.fcmToken);

    if (deviceTokens.length === 0) {
      setError('No valid device tokens found');
      return;
    }

    try {
      setSendingNotification(true);
      setError('');

      const data: Record<string, string> = {};
      notificationData.forEach(item => {
        if (item.key.trim() && item.value.trim()) {
          data[item.key.trim()] = item.value.trim();
        }
      });

      await apiService.sendDeviceNotification({
        deviceTokens,
        title: notificationTitle.trim(),
        body: notificationBody.trim(),
        data: Object.keys(data).length > 0 ? data : undefined,
      });

      setNotificationSuccess(`Notification sent to ${deviceTokens.length} device(s) successfully!`);
      setNotificationTitle('');
      setNotificationBody('');
      setNotificationData([]);

      // Auto-close after 2 seconds
      setTimeout(() => {
        setNotificationModalOpen(false);
        setNotificationSuccess('');
        setSelectedDevicesForNotification([]);
      }, 2000);
    } catch (err: any) {
      console.error('Error sending notification:', err);
      setError(err.message || 'Failed to send notification');
    } finally {
      setSendingNotification(false);
    }
  };

  const deleteUser = useCallback(async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      setLoading(true);
      await apiService.adminDeleteUser(userId);
      await loadUsers();
    } catch (err) {
      console.error(err);
      setError('Failed to delete user');
    } finally {
      setLoading(false);
    }
  }, [loadUsers]);

  // Cell Renderers
  const UserNameRenderer = useCallback((params: ICellRendererParams) => {
    return (
      <div>
        <div className="font-semibold text-sm text-gray-900">{params.data.fullName || '—'}</div>
        <div className="text-xs text-gray-500 mt-0.5">{params.data.email}</div>
      </div>
    );
  }, []);

  const getRoleColor = (role: string) => {
    const roleLower = (role || '').toLowerCase();
    if (roleLower.includes('admin')) return 'bg-red-100 text-red-800 border-red-300';
    if (roleLower.includes('user')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (roleLower.includes('moderator')) return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const RoleRenderer = useCallback((params: ICellRendererParams) => {
    return (
      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getRoleColor(params.value)}`}>
        {params.value || 'user'}
      </span>
    );
  }, []);

  const ActiveRenderer = useCallback((params: ICellRendererParams) => {
    const isActive = params.value === true || params.value === 'true';
    return (
      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${
        isActive ? 'bg-green-100 text-green-800 border-green-300' : 'bg-gray-100 text-gray-800 border-gray-300'
      }`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  }, []);

  const ActionsRenderer = useCallback((params: ICellRendererParams) => {
    const user = params.data;
    return (
      <div className="flex items-center space-x-1">
        <button
          onClick={() => showDevices(user.userId, user)}
          className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          title="View Devices"
        >
          <FontAwesomeIcon icon={faMobileScreen} className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => console.log('edit', user.userId)}
          className="p-1.5 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
          title="Edit User"
        >
          <FontAwesomeIcon icon={faEdit} className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => deleteUser(user.userId)}
          className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          title="Delete User"
        >
          <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }, [deleteUser, currentUserForDevices]);

  const columns: ColDef[] = useMemo(() => [
    {
      field: 'fullName',
      headerName: 'User',
      flex: 1,
      minWidth: 250,
      cellRenderer: UserNameRenderer,
      autoHeight: true,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 140,
      cellRenderer: RoleRenderer,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      cellRenderer: ActiveRenderer,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'devices',
      headerName: 'Devices',
      width: 110,
      valueGetter: (params: any) => {
        const data = params.data;
        if (data?.devicesCount !== undefined) return data.devicesCount;
        if (data?.devices?.length !== undefined) return data.devices.length;
        return 0;
      },
      cellStyle: { textAlign: 'center' },
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'credits',
      headerName: 'Credits',
      width: 110,
      valueGetter: (params: any) => params.data?.credits ?? 0,
      cellStyle: { textAlign: 'center' },
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      cellRenderer: ActionsRenderer,
      sortable: false,
      filter: false,
    }
  ], [UserNameRenderer, RoleRenderer, ActiveRenderer, ActionsRenderer]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
  }), []);

  const filteredUsers = roleFilter
    ? users.filter(u => u.role === roleFilter)
    : users;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-blue-100 dark:bg-primary-500/20 rounded-lg border border-blue-200 dark:border-primary-500/30">
            <FontAwesomeIcon icon={faUsers} className="h-6 w-6 text-blue-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Manage users, roles, and device registrations
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold">{filteredUsers.length}</span> of{' '}
            <span className="font-semibold">{users.length}</span> users
          </span>
          {selectedDevicesForNotification.length > 0 && (
            <button
              onClick={openNotificationModal}
              className="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 dark:hover:bg-green-700 transition-colors shadow-sm"
            >
              <FontAwesomeIcon icon={faBell} className="h-4 w-4 mr-2" />
              Send Notification ({selectedDevicesForNotification.length})
            </button>
          )}
          <button
            onClick={() => loadUsers()}
            className="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-primary-600 text-white rounded-lg font-medium hover:bg-blue-700 dark:hover:bg-primary-700 transition-colors shadow-sm"
          >
            <FontAwesomeIcon icon={faRefresh} className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Role Filter Chips */}
      {availableRoles.length > 0 && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-4">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Filter by Role:</span>
            <button
              onClick={() => setRoleFilter('')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                roleFilter === ''
                  ? 'bg-blue-600 dark:bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-dark-300/40 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-300/60 border border-gray-200 dark:border-dark-300/40'
              }`}
            >
              All Roles
            </button>
            {availableRoles.map((role) => (
              <button
                key={role}
                onClick={() => setRoleFilter(role)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                  roleFilter === role
                    ? 'bg-blue-600 dark:bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-dark-300/40 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-300/60 border border-gray-200 dark:border-dark-300/40'
                }`}
              >
                {role}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quick Search */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-4">
        <input
          type="text"
          placeholder="Quick search across all columns..."
          value={quickFilterText}
          onChange={(e) => setQuickFilterText(e.target.value)}
          className="block w-full px-4 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent placeholder-gray-500 dark:placeholder-gray-500"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/20 border-l-4 border-red-500 dark:border-danger-500 p-4 rounded">
          <div className="text-sm text-red-800 dark:text-danger-500 font-medium">{error}</div>
        </div>
      )}

      {/* AG Grid */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
        <div className="ag-theme-material" style={{ height: 'calc(100vh - 450px)', minHeight: '400px', width: '100%' }}>
          <AgGridReact
            rowData={filteredUsers}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={25}
            paginationPageSizeSelector={[10, 25, 50, 100]}
            loading={loading}
            rowHeight={60}
            animateRows={true}
            domLayout='normal'
            theme={themeQuartz}
            getRowId={(params: any) => params.data.id}
            quickFilterText={quickFilterText}
          />
        </div>
      </div>

      {/* Devices Modal */}
      {devicesDialogOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-400 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden border border-gray-200 dark:border-dark-300/40">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-primary-600 dark:to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 text-white">
                  <FontAwesomeIcon icon={faMobileScreen} className="h-6 w-6" />
                  <div>
                    <h3 className="text-lg font-semibold">
                      {currentUserForDevices?.fullName || currentUserForDevices?.email || 'User'} - Devices
                    </h3>
                    <p className="text-sm text-blue-100 dark:text-primary-100">
                      {selectedUserDevices ? selectedUserDevices.length : 0} registered device(s)
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setDevicesDialogOpen(false)}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors text-white"
                >
                  <FontAwesomeIcon icon={faX} className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6" style={{ height: '500px' }}>
              {loadingDevices ? (
                <div className="flex flex-col items-center justify-center h-full space-y-3">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-primary-500"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading devices...</p>
                </div>
              ) : selectedUserDevices && selectedUserDevices.length > 0 ? (
                <div className="ag-theme-material h-full">
                  <AgGridReact
                    rowData={selectedUserDevices}
                    columnDefs={[
                      {
                        field: 'select',
                        headerName: '',
                        width: 60,
                        sortable: false,
                        filter: false,
                        cellRenderer: (params: ICellRendererParams) => {
                          const device = params.data;
                          const isSelected = selectedDevicesForNotification.some(d => d._id === device._id);
                          const canSelect = device.enabled && device.fcmToken;
                          return (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!canSelect}
                              onChange={() => toggleDeviceSelection(device)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                              title={!canSelect ? 'Device is disabled or has no FCM token' : 'Select for notification'}
                            />
                          );
                        }
                      },
                      {
                        field: '_id',
                        headerName: 'Device ID',
                        flex: 2,
                        filter: 'agTextColumnFilter',
                      },
                      {
                        field: 'platform',
                        headerName: 'Platform',
                        flex: 1,
                        filter: 'agTextColumnFilter',
                        valueGetter: (params: any) => params.data?.deviceInfo?.platform || params.data?.platform || '—',
                      },
                      {
                        field: 'enabled',
                        headerName: 'Status',
                        width: 120,
                        cellRenderer: (params: ICellRendererParams) => {
                          const enabled = params.data?.enabled;
                          return (
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${
                              enabled ? 'bg-green-100 text-green-800 border-green-300' : 'bg-gray-100 text-gray-800 border-gray-300'
                            }`}>
                              {enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          );
                        }
                      },
                      {
                        field: 'fcmToken',
                        headerName: 'Push Token',
                        width: 120,
                        cellRenderer: (params: ICellRendererParams) => {
                          const hasToken = !!params.data?.fcmToken;
                          return (
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${
                              hasToken ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-gray-100 text-gray-500 border-gray-300'
                            }`}>
                              {hasToken ? 'Available' : 'None'}
                            </span>
                          );
                        }
                      },
                      {
                        field: 'lastSeenAt',
                        headerName: 'Last Seen',
                        flex: 1.5,
                        valueGetter: (params: any) => params.data?.lastSeenAt || params.data?.createdAt || '—',
                        valueFormatter: (params: any) => {
                          const date = params.value;
                          if (!date || date === '—') return '—';
                          try {
                            return new Date(date).toLocaleString();
                          } catch {
                            return date;
                          }
                        }
                      }
                    ]}
                    defaultColDef={{ sortable: true, resizable: true, filter: true }}
                    pagination
                    paginationPageSize={10}
                    theme={themeQuartz}
                    getRowId={(params: any) => params.data?._id || params.data?.id || String(Math.random())}
                  />
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center h-full space-y-2">
                  <p className="text-red-600 dark:text-danger-500 font-semibold">Error loading devices</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-600 dark:text-gray-400">No devices found</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-300/40 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <button
                  onClick={selectAllDevices}
                  disabled={!selectedUserDevices || selectedUserDevices.filter(d => d.enabled && d.fcmToken).length === 0}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-dark-300/40 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-dark-300/60 transition-colors disabled:opacity-50"
                >
                  Select All Enabled
                </button>
                {selectedDevicesForNotification.length > 0 && (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedDevicesForNotification.length} device(s) selected for notification
                  </span>
                )}
              </div>
              <button
                onClick={() => setDevicesDialogOpen(false)}
                className="px-4 py-2 bg-blue-600 dark:bg-primary-600 text-white rounded-lg font-medium hover:bg-blue-700 dark:hover:bg-primary-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selected Devices Indicator */}
      {selectedDevicesForNotification.length > 0 && !devicesDialogOpen && !notificationModalOpen && (
        <div className="fixed bottom-6 right-6 z-40">
          <div className="bg-white dark:bg-dark-400 rounded-lg shadow-xl border border-gray-200 dark:border-dark-300/40 p-4 max-w-sm">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Selected Devices ({selectedDevicesForNotification.length})
              </h4>
              <button
                onClick={clearAllSelectedDevices}
                className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                Clear All
              </button>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1 mb-3">
              {selectedDevicesForNotification.slice(0, 5).map(device => (
                <div key={device._id} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-dark-300/40 rounded px-2 py-1">
                  <span className="text-gray-700 dark:text-gray-300 truncate flex-1">
                    {device.userName || device._id.slice(-8)}
                  </span>
                  <button
                    onClick={() => removeDeviceFromSelection(device._id)}
                    className="ml-2 text-gray-400 hover:text-red-500"
                  >
                    <FontAwesomeIcon icon={faX} className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
              {selectedDevicesForNotification.length > 5 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  +{selectedDevicesForNotification.length - 5} more
                </div>
              )}
            </div>
            <button
              onClick={openNotificationModal}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
            >
              <FontAwesomeIcon icon={faBell} className="h-4 w-4" />
              <span>Send Notification</span>
            </button>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-400 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden border border-gray-200 dark:border-dark-300/40">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 dark:from-green-600 dark:to-green-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 text-white">
                  <FontAwesomeIcon icon={faBell} className="h-6 w-6" />
                  <div>
                    <h3 className="text-lg font-semibold">Send Push Notification</h3>
                    <p className="text-sm text-green-100">
                      Sending to {selectedDevicesForNotification.length} device(s)
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setNotificationModalOpen(false)}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors text-white"
                >
                  <FontAwesomeIcon icon={faX} className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
              {notificationSuccess && (
                <div className="bg-green-50 dark:bg-green-500/20 border border-green-200 dark:border-green-500/30 rounded-lg p-4">
                  <p className="text-sm text-green-800 dark:text-green-400 font-medium">{notificationSuccess}</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-500/30 rounded-lg p-4">
                  <p className="text-sm text-red-800 dark:text-red-400 font-medium">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  maxLength={100}
                  placeholder="Enter notification title"
                  className="block w-full px-4 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent placeholder-gray-500"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{notificationTitle.length}/100</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Body <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={notificationBody}
                  onChange={(e) => setNotificationBody(e.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder="Enter notification message"
                  className="block w-full px-4 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent placeholder-gray-500 resize-none"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{notificationBody.length}/500</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Custom Data (Optional)
                  </label>
                  <button
                    onClick={addDataField}
                    className="text-sm text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 flex items-center space-x-1"
                  >
                    <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                    <span>Add Field</span>
                  </button>
                </div>
                {notificationData.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={item.key}
                      onChange={(e) => updateDataField(index, 'key', e.target.value)}
                      placeholder="Key"
                      className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent placeholder-gray-500"
                    />
                    <input
                      type="text"
                      value={item.value}
                      onChange={(e) => updateDataField(index, 'value', e.target.value)}
                      placeholder="Value"
                      className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent placeholder-gray-500"
                    />
                    <button
                      onClick={() => removeDataField(index)}
                      className="p-1.5 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <FontAwesomeIcon icon={faMinus} className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Selected Devices Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Recipients
                </label>
                <div className="bg-gray-50 dark:bg-dark-300/40 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <div className="flex flex-wrap gap-2">
                    {selectedDevicesForNotification.map(device => (
                      <span
                        key={device._id}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300"
                      >
                        {device.userName || device._id.slice(-8)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-300/40 flex justify-end space-x-3">
              <button
                onClick={() => setNotificationModalOpen(false)}
                disabled={sendingNotification}
                className="px-4 py-2 bg-gray-100 dark:bg-dark-300/40 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-dark-300/60 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={sendNotification}
                disabled={sendingNotification || !notificationTitle.trim() || !notificationBody.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {sendingNotification ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} className="h-4 w-4" />
                    <span>Send Notification</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
